import { Button, Flex, Paper, TextInput, Text, Loader } from "@mantine/core";
import { useAppDispatch, useAppSelector } from "../hooks";
import { RootState } from "../store";
import {
  displayErrorMessage,
  displayServerErrorMessage,
} from "../shared/ErrorMessage";
import {
  CreateGuestRequest,
  useCreateGuestMutation,
} from "../slices/api/guestsApi";
import {
  initialize,
  MinimalGuest,
  updateField,
} from "../slices/form/createGuest";
import { useEffect } from "react";
import { GuestStatus } from "../slices/types";
import { GuestStatusSegmentedControl } from "../shared/components/GuestsStatusSegmentedControl";
import { isValidEmail } from "../shared/utility";

interface Props {
  title: string;
  ventId: string;
  emailRequired: boolean;
  defaultStatus: GuestStatus;
  primaryGuestId?: string; // if supplied, then this person is a plus one
  primaryGuestFirstName?: string; // only to help with caching
  primaryGuestLastName?: string; // only to help with caching
  description?: string; // shows helpful info to the user
  onClose: () => void;
}
const CreateGuest = (props: Props) => {
  const { data: minimalGuest } = useAppSelector(
    (state: RootState) => state.createGuestForm
  );
  const [createGuest] = useCreateGuestMutation();

  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(initialize({ status: props.defaultStatus } as MinimalGuest));
  }, []);

  const handleClick = async () => {
    if (minimalGuest === undefined) {
      console.error("Guest is undefined but should not be");
      return;
    }
    if (minimalGuest.firstName == null) {
      displayErrorMessage("First name is required.");
      return;
    }
    if (props.emailRequired) {
      if (minimalGuest.email == null) {
        displayErrorMessage("Email is required.");
        return;
      } else if (!isValidEmail(minimalGuest.email)) {
        displayErrorMessage("Email is not valid.");
        return;
      }
    }

    const request: CreateGuestRequest = {
      ventId: props.ventId,
      email: minimalGuest.email,
      firstName: minimalGuest.firstName!,
      lastName: minimalGuest.lastName,
      primaryGuestId: props.primaryGuestId,
      primaryGuestFirstName: props.primaryGuestFirstName,
      primaryGuestLastName: props.primaryGuestLastName,
      status: minimalGuest.status,
    };

    try {
      await createGuest(request).unwrap();
      props.onClose();
    } catch (error) {
      displayServerErrorMessage(error);
    }
  };

  if (!minimalGuest) {
    return <Loader color="blue" />;
  }

  return (
    <>
      <Paper>
        <Text size="lg">{props.title}</Text>
        {!props.emailRequired && (
          <Text size="sm" mt="md">
            When you add a plus one, you are responsible for their RSVP. They
            will not have access to the event website, and will not receive
            event communications. If they want to manage their RSVP, they should
            sign up on the event home page.
          </Text>
        )}
        <TextInput
          required
          mt="md"
          label="First Name"
          value={minimalGuest.firstName ?? ""}
          onChange={(guest) => {
            dispatch(
              updateField({
                field: "firstName",
                value: guest.currentTarget.value,
              })
            );
          }}
        />
        <TextInput
          mt="md"
          label="Last Name"
          value={minimalGuest.lastName ?? ""}
          onChange={(guest) => {
            dispatch(
              updateField({
                field: "lastName",
                value: guest.currentTarget.value,
              })
            );
          }}
        />
        {props.emailRequired && (
          <TextInput
            required
            mt="md"
            label="Email"
            type="email"
            value={minimalGuest.email ?? ""}
            onChange={(guest) => {
              dispatch(
                updateField({
                  field: "email",
                  value: guest.currentTarget.value,
                })
              );
            }}
          />
        )}
        <GuestStatusSegmentedControl
          mt="lg"
          value={minimalGuest.status}
          onChange={(value) =>
            dispatch(
              updateField({
                field: "status",
                value: value,
              })
            )
          }
        />
        <Flex justify="flex-end">
          <Button mt="md" mr="md" onClick={props.onClose}>
            Cancel
          </Button>
          <Button mt="md" onClick={handleClick}>
            Save
          </Button>
        </Flex>
      </Paper>
    </>
  );
};

export default CreateGuest;
